import { DashboardBanner } from '@/lib/components/dashboard-banner';
import { BannerMessageContext } from '@/lib/context/banner-message';
import { useBannerMessageSSE } from '@/lib/hooks/use-banner-message-sse';
import { useBroadcastChannel } from '@/lib/hooks/use-broadcast-channel';
import {
  OrganisationManagerContext,
  useOrganisationManager,
} from '@/lib/hooks/use-organisation-manager';
import { LocaleProvider, useAppLocale } from '@/lib/i18n/LocaleProvider';
import { DEFAULT_LOCALE, SupportedLocale } from '@/lib/i18n/config';
import { readLocaleCookie, readLocaleFromCookieHeader } from '@/lib/i18n/cookies';
import { dateFnsLocaleFor } from '@/lib/i18n/locale-utils';
import browserDatabase, { BROWSER_DATABASE_KEYS } from '@/lib/utils/browser-database';
import { Close } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import getTheme from '@/lib/utils/theme';
import { NextPage } from 'next';
import type { AppContext, AppProps } from 'next/app';
import App from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import 'styles/global.scss';
import { Provider as UrqlProvider } from 'urql';
import urqlClient from '../lib/utils/urql-client';
import { useTranslations } from 'next-intl';

const {
  publicRuntimeConfig: { GTM_ENABLED },
} = getConfig();

const BANNER_MESSAGE_DISMISSED_CHANNEL = 'banner-message-dismissed';

const SNACKBAR_AUTO_HIDE_DURATION = 2000;
const SNACKBAR_ACTIONS = (provider: SnackbarProvider | null, snackbarId: string | number) => (
  <IconButton onClick={() => provider?.closeSnackbar(snackbarId)} sx={{ color: '#FFF' }}>
    <Close />
  </IconButton>
);

/**
 * Wraps the default URQL Provider by passing the App's URQL client
 * @param {ReactNode} children
 */
const UrqlProviderWrapper = ({ children }: { children: ReactNode }) => {
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslations('common');
  const client = useMemo(() => urqlClient(enqueueSnackbar, t), [enqueueSnackbar, t]);

  return <UrqlProvider value={client}>{children}</UrqlProvider>;
};

export type NextPageWithLayout<T> = NextPage<T> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component?: NextPageWithLayout<any>;
};

const BannerProviderWrapper = ({ children }: { children: ReactNode }) => {
  const { bannerMessage, initialised } = useBannerMessageSSE();
  const [showBannerMessage, setShowBannerMessage] = useState<boolean>(false);
  const { broadcast, addListener } = useBroadcastChannel<boolean>({
    channelName: BANNER_MESSAGE_DISMISSED_CHANNEL,
  });

  useEffect(() => {
    const cleanup = addListener(() => {
      // Another tab has dismissed the banner message
      setShowBannerMessage(false);
    });

    return () => {
      cleanup();
    };
  }, [addListener]);

  const setDismissed = () => {
    if (bannerMessage === null) {
      return;
    }
    browserDatabase.setItem<string>(BROWSER_DATABASE_KEYS.DISMISSED_BANNER_MESSAGE, bannerMessage);
    setShowBannerMessage(false);
    broadcast(true);
  };

  useEffect(() => {
    // If the banner message has changed, clear the dismissed state
    if (!initialised) {
      return;
    }

    if (!bannerMessage) {
      browserDatabase.deleteItem(BROWSER_DATABASE_KEYS.DISMISSED_BANNER_MESSAGE);
    }

    // Determine if the banner message has been dismissed
    const dismissedBannerMessage = browserDatabase.getItem<string>(
      BROWSER_DATABASE_KEYS.DISMISSED_BANNER_MESSAGE,
    );
    const bannerMessageIsDismissed = dismissedBannerMessage === bannerMessage;
    setShowBannerMessage(!!bannerMessage && !bannerMessageIsDismissed);
  }, [bannerMessage, initialised]);

  return (
    <BannerMessageContext.Provider
      value={{
        bannerMessage: showBannerMessage ? bannerMessage : null,
        setDismissed,
      }}
    >
      {children}
    </BannerMessageContext.Provider>
  );
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const initialLocale = (pageProps?.initialLocale as SupportedLocale | undefined) ?? DEFAULT_LOCALE;
  return (
    <LocaleProvider initialLocale={initialLocale}>
      <ProviderHaven Component={Component} pageProps={pageProps} />
    </LocaleProvider>
  );
}

type ProviderHavenProps = {
  Component: AppPropsWithLayout['Component'];
  pageProps: AppPropsWithLayout['pageProps'];
};

function ProviderHaven({ Component, pageProps }: ProviderHavenProps) {
  const { getLayout } = Component || {};
  const organisationManager = useOrganisationManager();
  const snackBarRef = useRef<SnackbarProvider>(null);
  const { locale } = useAppLocale();
  const dateFnsLocale = useMemo(() => dateFnsLocaleFor(locale), [locale]);
  const theme = useMemo(() => getTheme(locale), [locale]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
      <OrganisationManagerContext.Provider value={organisationManager}>
        {GTM_ENABLED && (
          <>
            <Head>
              <script
                id="gtm-head"
                dangerouslySetInnerHTML={{
                  __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-WVKPRLF');`,
                }}
              ></script>
            </Head>
            <noscript
              dangerouslySetInnerHTML={{
                __html: `
<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-WVKPRLF"
height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
              }}
            ></noscript>
          </>
        )}
        <SnackbarProvider
          ref={snackBarRef}
          maxSnack={3}
          autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          action={(id) => SNACKBAR_ACTIONS(snackBarRef?.current, id)}
        >
          <ThemeProvider theme={theme}>
            <UrqlProviderWrapper>
              <BannerProviderWrapper>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                  }}
                >
                  <DashboardBanner />
                  {getLayout ? (
                    getLayout(<Component {...pageProps} />)
                  ) : (
                    <Component {...pageProps} />
                  )}
                </Box>
              </BannerProviderWrapper>
            </UrqlProviderWrapper>
          </ThemeProvider>
        </SnackbarProvider>
      </OrganisationManagerContext.Provider>
    </LocalizationProvider>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const cookieLocale =
    readLocaleFromCookieHeader(appContext.ctx.req?.headers.cookie) ?? readLocaleCookie();
  const initialLocale = cookieLocale ?? DEFAULT_LOCALE;
  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      initialLocale,
    },
  };
};

export default MyApp;
